import { getNamedType, isInputObjectType } from 'graphql';
import classifyLogicalToken from './classifyLogicalToken.js';
import getMatchingLeafPrefix from './getMatchingLeafPrefix.js';
import normalizeOperatorToken from './normalizeOperatorToken.js';

const analyzeFilterInputType = ({ inputType, leafFieldNames, visitedTypes = new Set() }) => {
  const metadata = {
    directEqualityFieldCount: 0,
    nestedFieldCount: 0,
    postfixFieldCount: 0,
    fieldOperators: new Map(),
    logicalTokens: {
      and: new Set(),
      or: new Set(),
      not: new Set()
    },
    rawOperatorTokensBySlug: new Map()
  };

  const addFieldOperator = ({ fieldName, operatorSlug, rawOperatorToken }) => {
    if (!fieldName || !operatorSlug) {
      return;
    }

    if (!metadata.fieldOperators.has(fieldName)) {
      metadata.fieldOperators.set(fieldName, new Set());
    }

    metadata.fieldOperators.get(fieldName).add(operatorSlug);

    if (!metadata.rawOperatorTokensBySlug.has(operatorSlug)) {
      metadata.rawOperatorTokensBySlug.set(operatorSlug, new Set());
    }

    metadata.rawOperatorTokensBySlug.get(operatorSlug).add(rawOperatorToken || operatorSlug);
  };

  const visitInputType = (currentInputType) => {
    if (!isInputObjectType(currentInputType) || visitedTypes.has(currentInputType.name)) {
      return;
    }

    visitedTypes.add(currentInputType.name);

    Object.values(currentInputType.getFields()).forEach((inputField) => {
      const logicalToken = classifyLogicalToken(inputField.name);

      if (logicalToken) {
        metadata.logicalTokens[logicalToken].add(inputField.name);

        const nestedType = getNamedType(inputField.type);

        if (isInputObjectType(nestedType)) {
          visitInputType(nestedType);
        }

        return;
      }

      const namedType = getNamedType(inputField.type);

      if (isInputObjectType(namedType)) {
        const childFields = Object.values(namedType.getFields()).filter((childField) => !classifyLogicalToken(childField.name));
        const childOperatorMatches = childFields.map((childField) => ({
          rawOperatorToken: childField.name,
          operatorSlug: normalizeOperatorToken(childField.name) || childField.name
        }));

        if (childOperatorMatches.length > 0) {
          metadata.nestedFieldCount += 1;
          childOperatorMatches.forEach(({ operatorSlug, rawOperatorToken }) => {
            addFieldOperator({ fieldName: inputField.name, operatorSlug, rawOperatorToken });
          });
          return;
        }

        visitInputType(namedType);
        return;
      }

      const matchingLeafPrefix = getMatchingLeafPrefix(inputField.name, leafFieldNames);

      if (matchingLeafPrefix) {
        const rawOperatorToken = inputField.name.slice(matchingLeafPrefix.length + 1);
        const operatorSlug = normalizeOperatorToken(rawOperatorToken) || rawOperatorToken;

        metadata.postfixFieldCount += 1;
        addFieldOperator({ fieldName: matchingLeafPrefix, operatorSlug, rawOperatorToken });
        return;
      }

      if (leafFieldNames.has(inputField.name)) {
        metadata.directEqualityFieldCount += 1;
        addFieldOperator({ fieldName: inputField.name, operatorSlug: 'eq', rawOperatorToken: 'eq' });
      }
    });
  };

  visitInputType(inputType);

  return metadata;
};

export default analyzeFilterInputType;
